import { useEffect, useId, useState } from "react";
import type { OverviewKpiSeries } from "@nexus/contracts";
import {
  Bar,
  ComposedChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { BarShapeProps, TooltipContentProps } from "recharts";
import { MAX_CHART_METRICS } from "../../../stores/overviewFilters.store.js";
import {
  AXIS_TICK_FRACTIONS,
  computeMetricDomains,
  formatBarLabel,
  formatRawMetric,
  metricAxisTicks,
  rawMetricKey,
  type ChartDatum,
  type ResolvedMetric,
} from "../chartModel.js";
import {
  CategoryTick,
  CHART_GRID_STROKE,
  CHART_LABEL_FONT_WEIGHT,
  CHART_TICK_FONT_SIZE,
  CHART_TOP_MARGIN,
  ChartTooltip,
} from "../chartPrimitives.js";
import { BarSeriesGradientDefs } from "../BarSeriesGradientDefs.js";
import { barGradientHoverId, barGradientId } from "../chartBarGradients.js";
import { RoundedTopBar } from "../RoundedTopBar.js";
import { usePrefersReducedMotion } from "../usePrefersReducedMotion.js";

/**
 * Metric count at which a category band can no longer host full-width bars and
 * their value labels, so both step down a size. MAX_CHART_METRICS caps this at 3.
 */
const DENSE_METRIC_COUNT = 3;

/** Fixed bar widths — bars never stretch to fill the band. */
const BAR_SIZE = 40;
const BAR_SIZE_DENSE = 24;

/**
 * Gap between the bars inside one category group. Value labels are wider than
 * the bars they sit on, so the denser layout buys them room to breathe rather
 * than crowding the neighbouring bar.
 */
const BAR_GAP = 6;
const BAR_GAP_DENSE = 10;

/**
 * Slice of each category band left empty, separating adjacent groups. Tuned so
 * a full group still fits at VISIBLE_CATEGORIES bands while the gap between
 * groups stays clearly wider than the intra-group gap.
 */
const BAR_CATEGORY_GAP = "20%";

const BAR_LABEL_FONT_SIZE = 12;
const BAR_LABEL_FONT_SIZE_DENSE = 10;

/**
 * Vertical stagger applied to every other series' value label. A currency label
 * is wider than the bar it sits on, so labels over two adjacent equal-height
 * bars would otherwise overlap; lifting alternate ones keeps every value
 * readable without rotating or abbreviating them.
 *
 * Applied as `dy` on the label text — `LabelList`'s `offset` prop is ignored
 * once a `valueAccessor` is supplied.
 */
const BAR_LABEL_STAGGER = 14;

/** Bar grow from baseline — fast enough for Operate mode, visible on first paint. */
const BAR_ANIMATION_MS = 450;

/** Offset each series slightly so grouped bars read as a sequence, not a pop. */
const BAR_ANIMATION_SERIES_STAGGER_MS = 60;

function barSizeFor(metricCount: number): number {
  return metricCount >= DENSE_METRIC_COUNT ? BAR_SIZE_DENSE : BAR_SIZE;
}

function barGapFor(metricCount: number): number {
  return metricCount >= DENSE_METRIC_COUNT ? BAR_GAP_DENSE : BAR_GAP;
}

function barLabelFontSizeFor(metricCount: number): number {
  return metricCount >= DENSE_METRIC_COUNT ? BAR_LABEL_FONT_SIZE_DENSE : BAR_LABEL_FONT_SIZE;
}

function barLabelDyFor(metricIndex: number): number {
  const isStaggered = metricIndex % 2 === 1;
  return isStaggered ? -BAR_LABEL_STAGGER : 0;
}

/** Recharts bar shape — radius clamping lives in {@link RoundedTopBar}. */
function renderRoundedTopBar({ x, y, width, height, fill }: BarShapeProps) {
  return <RoundedTopBar x={x} y={y} width={width} height={height} fill={fill} />;
}

/**
 * Width reserved for each KPI's axis. Recharts lays same-side axes out side by side,
 * so this multiplies with the metric count. Sized for the widest tick a currency KPI
 * produces (`$553.3K` ≈ 52px at 13px bold) plus the gutter below, so neighbouring
 * axes never let their tick columns touch.
 */
const AXIS_WIDTH = 78;

/** Gap between a tick label and its axis line — the visible gutter between axes. */
const AXIS_TICK_MARGIN = 10;

/** Keeps the outermost axis's widest tick off the card's edge, on either side. */
const AXIS_PANE_EDGE_MARGIN = 8;

/** Room for the last category's value labels, which overhang their bars. */
const CHART_RIGHT_MARGIN = 24;

/** Clears the descenders on the category labels below the axis. */
const CHART_BOTTOM_MARGIN = 16;

/** Band reserved for the category axis and its labels. */
const X_AXIS_HEIGHT = 56;

/**
 * Horizontal slot one topic gets. Recharts always spreads categories across the
 * full plot width, so with only a handful of topics the groups would drift apart
 * until they read as unrelated. Capping the plot at this budget per topic keeps
 * the spacing constant and packs the groups against the axes instead.
 */
const CATEGORY_BAND_WIDTH = 160;

/** Category bands that fill the visible plot; past this the plot scrolls sideways. */
const VISIBLE_CATEGORIES = 10;

/** Width one pinned axis pane occupies, its outer edge margin included. */
function axisPaneWidth(metricCount: number): number {
  return AXIS_PANE_EDGE_MARGIN + metricCount * AXIS_WIDTH;
}

/**
 * Width of the scrolling plot. The viewport between the two pinned axis panes holds
 * VISIBLE_CATEGORIES bands, so beyond that the plot grows in proportion — each band
 * keeps the width it had at a full viewport and the overflow is scrolled to. Below it
 * the plot fills the viewport, capped at a full band per topic so sparse topics stay
 * packed against the left axis. A CSS expression, since the viewport width is only known
 * to the browser; ResponsiveContainer then measures the resolved pixels as before.
 */
function plotWidth(paneWidths: number, categoryCount: number): string {
  const scale = Math.max(categoryCount, VISIBLE_CATEGORIES) / VISIBLE_CATEGORIES;
  const cap = categoryCount * CATEGORY_BAND_WIDTH + CHART_RIGHT_MARGIN;
  return `min(calc((100% - ${paneWidths}px) * ${scale}), ${cap}px)`;
}

/**
 * Opaque id binding a metric's bars to its scale. The axes themselves are DOM columns
 * in {@link KpiAxisColumns}, which own their left-to-right order, so recharts' own
 * `yAxisId` sort no longer places anything.
 *
 * Counts down from the cap rather than from `metrics.length` so a metric's axis id
 * never depends on how many others are selected: adding a third KPI leaves the first
 * two axes registered under the ids they already had.
 */
function axisIdFor(metricIndex: number): string {
  return `axis-${MAX_CHART_METRICS - metricIndex}`;
}

/**
 * Left edge of a KPI's axis column within its own pane, in metric order. The left pane
 * carries its edge margin before the columns, the right pane after them, so both panes
 * keep their outermost ticks the same distance off the card.
 */
function axisColumnOffset(side: AxisSide, metricIndex: number): number {
  return (side === "left" ? AXIS_PANE_EDGE_MARGIN : 0) + metricIndex * AXIS_WIDTH;
}

/** Height recharts takes off the top of the chart before the plot starts. */
const PLOT_TOP_INSET = CHART_TOP_MARGIN + BAR_LABEL_STAGGER;

/** Height below the plot: the category axis band plus the margin under it. */
const PLOT_BOTTOM_INSET = CHART_BOTTOM_MARGIN + X_AXIS_HEIGHT;

/**
 * Offset from the bottom of the chart box to the tick row at `fraction` of the plot.
 * Every KPI's domain maps onto the same plot height and shares one set of tick
 * fractions, so one row serves every axis at once — the pinned axis labels and the
 * guide lines are placed from this single formula rather than from recharts' layout.
 */
function tickRowOffset(fraction: number): string {
  return `calc(${PLOT_BOTTOM_INSET}px + ${fraction} * (100% - ${PLOT_TOP_INSET + PLOT_BOTTOM_INSET}px))`;
}

/**
 * Horizontal rule at each axis tick row, drawn behind the chart rather than inside it.
 * Recharts would clip these to the plot, which stops short of the card whenever the
 * topics do not fill the full width. The lowest rule doubles as the category axis line,
 * which is why the axis draws none of its own.
 */
function PlotGuideLines() {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      {AXIS_TICK_FRACTIONS.map((fraction) => (
        <div
          key={fraction}
          className="absolute inset-x-0 h-px"
          style={{
            bottom: tickRowOffset(fraction),
            backgroundColor: CHART_GRID_STROKE,
          }}
        />
      ))}
    </div>
  );
}

type AxisSide = "left" | "right";

interface KpiAxisColumnsProps {
  side: AxisSide;
  metrics: ResolvedMetric[];
  domains: Record<string, [number, number]>;
  currencySymbol: string;
  seriesClassNameFor: (metricId: string) => string | undefined;
  onHoverMetric: (metricId: string | null) => void;
}

/**
 * One pane of KPI axes, as DOM columns pinned beside the scrolling plot. Recharts draws
 * its axes into the same SVG as the bars, so they would scroll away with them; these are
 * built from the layout constants instead and stay put. Each column doubles as the
 * hover target that dims the other series — recharts strips mouse events from <YAxis>.
 *
 * The two panes are mirror images: each puts its axis line on the edge facing the plot
 * and hangs its tick labels away from it.
 */
function KpiAxisColumns({
  side,
  metrics,
  domains,
  currencySymbol,
  seriesClassNameFor,
  onHoverMetric,
}: KpiAxisColumnsProps) {
  const facingPlot = side === "left" ? "right-0" : "left-0";
  const labelPadding = side === "left" ? { paddingRight: AXIS_TICK_MARGIN } : { paddingLeft: AXIS_TICK_MARGIN };
  return (
    <div
      data-testid="kpi-axis-pane"
      data-side={side}
      className={`sticky ${side === "left" ? "left-0" : "right-0"} z-10 h-full shrink-0 bg-card`}
      style={{ width: axisPaneWidth(metrics.length) }}
    >
      {metrics.map((m, index) => {
        const ticks = metricAxisTicks(domains[m.id]!);
        return (
          <div
            key={m.id}
            data-testid="kpi-axis-column"
            data-metric-id={m.id}
            className={`absolute inset-y-0 ${seriesClassNameFor(m.id) ?? ""}`}
            style={{ left: axisColumnOffset(side, index), width: AXIS_WIDTH }}
            onMouseEnter={() => onHoverMetric(m.id)}
            onMouseLeave={() => onHoverMetric(null)}
          >
            <div
              className={`absolute ${facingPlot} w-px`}
              style={{
                top: PLOT_TOP_INSET,
                bottom: PLOT_BOTTOM_INSET,
                backgroundColor: m.colorVar,
              }}
            />
            {AXIS_TICK_FRACTIONS.map((fraction, tickIndex) => (
              // Positioned by its bottom edge, then pushed down half its own height so
              // the text centres on the tick row the guide line is drawn on.
              <span
                key={fraction}
                className={`absolute ${facingPlot} translate-y-1/2 whitespace-nowrap leading-none tabular-nums`}
                style={{
                  bottom: tickRowOffset(fraction),
                  ...labelPadding,
                  fontSize: CHART_TICK_FONT_SIZE,
                  fontWeight: CHART_LABEL_FONT_WEIGHT,
                  color: m.colorVar,
                }}
              >
                {formatRawMetric(m, ticks[tickIndex]!, currencySymbol)}
              </span>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/** Series the pointer is not on lose their colour but hold their position. */
const DIMMED_SERIES_CLASS = "opacity-40 grayscale";
const SERIES_TRANSITION_CLASS = "transition-[filter,opacity] duration-150";

function seriesClassName(isDimmed: boolean, prefersReducedMotion: boolean): string | undefined {
  const classes = [
    prefersReducedMotion ? null : SERIES_TRANSITION_CLASS,
    isDimmed ? DIMMED_SERIES_CLASS : null,
  ].filter(Boolean);
  return classes.length > 0 ? classes.join(" ") : undefined;
}

interface BarsViewProps {
  data: ChartDatum[];
  metrics: ResolvedMetric[];
  currencySymbol: string;
  kpiSeries: OverviewKpiSeries[];
  /** Raw Topic_Title of the open detail drawer — its category label gets a chip. */
  selectedTopic?: string | null;
  onTopicSelect?: (topicTitle: string) => void;
}

/**
 * Grouped bar chart — one bar per selected metric per topic. Each KPI plots its raw
 * values against its OWN axis, scaled to that KPI's min/max across the charted topics,
 * so bars are comparable within a series but never across series. Spend's axis is
 * pinned left and every other KPI's is pinned right; only the bars scroll between them.
 */
export function BarsView({
  data,
  metrics,
  currencySymbol,
  kpiSeries,
  selectedTopic,
  onTopicSelect,
}: BarsViewProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [hoveredMetricId, setHoveredMetricId] = useState<string | null>(null);
  const isDimmed = (metricId: string) => hoveredMetricId !== null && hoveredMetricId !== metricId;

  // Recharts replays the grow animation on every render and withholds value labels
  // until it finishes, so hovering would blink the labels off. Retire the animation
  // once it has run, and arm it again only when the chart's content actually changes.
  //
  // Only the last series may retire it. Recharts restarts a bar's animation whenever its
  // props change identity, and turning `isAnimationActive` off jumps that bar straight to
  // its final size — so flipping this on the FIRST series to finish would snap the ones
  // still growing behind it. The series are staggered by a constant step and share one
  // duration, so the last one always finishes last.
  const [hasGrown, setHasGrown] = useState(false);
  useEffect(() => setHasGrown(false), [data, metrics]);

  const chartInstanceId = useId().replace(/:/g, "");
  const barSize = barSizeFor(metrics.length);
  const barGap = barGapFor(metrics.length);
  const barLabelFontSize = barLabelFontSizeFor(metrics.length);
  const domains = computeMetricDomains(data, metrics);
  // Bars keep iterating the full list, so draw order and axis ids are unaffected by the split.
  const leftAxisMetrics = metrics.filter((m) => m.axis === "left");
  const rightAxisMetrics = metrics.filter((m) => m.axis === "right");
  const seriesClassNameFor = (metricId: string) =>
    seriesClassName(isDimmed(metricId), prefersReducedMotion);
  return (
    // A click inside the chart focuses whichever recharts element it lands on — the
    // `role="application"` svg, or one of the `tabindex="-1"` z-index portal groups —
    // and the base `outline-ring` rule then paints a ring around it. Suppressing the
    // mousedown default keeps focus where it was; a :focus-visible rule would not,
    // since Chrome counts a click on `role="application"` as focus-visible once the
    // keyboard has been used. Tabbing to the chart still focuses it and still rings.
    //
    <div className="h-full w-full" onMouseDown={(event) => event.preventDefault()}>
      {/* The axis panes scroll with the plot rather than floating over it, so a
          horizontal scrollbar shortens them all by the same amount and the tick rows
          stay level with the bars. */}
      <div className="flex h-full w-full overflow-x-auto overflow-y-hidden">
        <KpiAxisColumns
          side="left"
          metrics={leftAxisMetrics}
          domains={domains}
          currencySymbol={currencySymbol}
          seriesClassNameFor={seriesClassNameFor}
          onHoverMetric={setHoveredMetricId}
        />
        {/* Inline width: it is data-driven (topic and metric counts), so it cannot be
            expressed as a Tailwind class. */}
        <div
          className="relative h-full shrink-0"
          style={{
            width: plotWidth(
              axisPaneWidth(leftAxisMetrics.length) + axisPaneWidth(rightAxisMetrics.length),
              data.length,
            ),
          }}
        >
          <PlotGuideLines />
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{
                top: CHART_TOP_MARGIN + BAR_LABEL_STAGGER,
                right: CHART_RIGHT_MARGIN,
                bottom: CHART_BOTTOM_MARGIN,
                // The axis gutter belongs to the pinned columns, so the plot starts at
                // this box's left edge.
                left: 0,
              }}
              barGap={barGap}
              barCategoryGap={BAR_CATEGORY_GAP}
              onClick={(state) => {
                const label = state?.activeLabel;
                if (label != null && onTopicSelect) onTopicSelect(String(label));
              }}
              style={onTopicSelect ? { cursor: "pointer" } : undefined}
            >
              <BarSeriesGradientDefs chartInstanceId={chartInstanceId} metrics={metrics} />
              <XAxis
                dataKey="category"
                interval={0}
                height={X_AXIS_HEIGHT}
                tick={<CategoryTick selectedCategory={selectedTopic} />}
                tickLine={false}
                // The lowest rule in <PlotGuideLines> sits at this exact height and runs
                // the full width of the card, so recharts drawing its own would only add
                // a short duplicate under the plot.
                axisLine={false}
              />
              {/* Scale only — <KpiAxisColumns> draws the axis itself, pinned outside the
                scroll. Zero width so it claims none of the plot. */}
              {metrics.map((m, index) => (
                <YAxis
                  key={m.id}
                  yAxisId={axisIdFor(index)}
                  domain={domains[m.id]!}
                  width={0}
                  tick={false}
                  tickLine={false}
                  axisLine={false}
                />
              ))}
              <Tooltip
                cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
                content={(props) => (
                  <ChartTooltip
                    {...(props as TooltipContentProps<number, string>)}
                    metrics={metrics}
                    currencySymbol={currencySymbol}
                    kpiSeries={kpiSeries}
                  />
                )}
              />
              {metrics.map((m, index) => {
                const gradientFill = `url(#${barGradientId(chartInstanceId, m.id)})`;
                const hoverGradientFill = `url(#${barGradientHoverId(chartInstanceId, m.id)})`;
                return (
                  <Bar
                    key={m.id}
                    yAxisId={axisIdFor(index)}
                    dataKey={rawMetricKey(m.id)}
                    name={m.label}
                    className={seriesClassName(isDimmed(m.id), prefersReducedMotion)}
                    onMouseEnter={() => setHoveredMetricId(m.id)}
                    onMouseLeave={() => setHoveredMetricId(null)}
                    fill={gradientFill}
                    shape={renderRoundedTopBar}
                    // The hovered category lights up every series' bar, and recharts draws
                    // those active shapes in their own portal, out of reach of this layer's
                    // dim filter. A dimmed series simply forgoes the highlight instead.
                    activeBar={
                      isDimmed(m.id)
                        ? false
                        : (props) => renderRoundedTopBar({ ...props, fill: hoverGradientFill })
                    }
                    barSize={barSize}
                    isAnimationActive={!prefersReducedMotion && !hasGrown}
                    animationDuration={BAR_ANIMATION_MS}
                    animationEasing="ease-out"
                    animationBegin={index * BAR_ANIMATION_SERIES_STAGGER_MS}
                    onAnimationEnd={
                      index === metrics.length - 1 ? () => setHasGrown(true) : undefined
                    }
                  >
                    <LabelList
                      position="top"
                      // Value labels are portaled into their own z-index layer, so they
                      // miss the class on the series group and carry their own copy.
                      className={seriesClassName(isDimmed(m.id), prefersReducedMotion)}
                      dy={barLabelDyFor(index)}
                      fontSize={barLabelFontSize}
                      fontWeight={CHART_LABEL_FONT_WEIGHT}
                      fill={m.colorVar}
                      valueAccessor={(entry) =>
                        formatBarLabel(
                          m,
                          entry.payload as ChartDatum,
                          currencySymbol,
                          domains[m.id]!,
                        )
                      }
                    />
                  </Bar>
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <KpiAxisColumns
          side="right"
          metrics={rightAxisMetrics}
          domains={domains}
          currencySymbol={currencySymbol}
          seriesClassNameFor={seriesClassNameFor}
          onHoverMetric={setHoveredMetricId}
        />
      </div>
    </div>
  );
}
